import classnames from 'classnames';

import { Breakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { Card } from '@hh.ru/magritte-ui-card';
import { Cell } from '@hh.ru/magritte-ui-cell';
import { CustomChip } from '@hh.ru/magritte-ui-chips';
import { DataSection, DataSectionOfType } from '@hh.ru/magritte-ui-suggest/types';
import { Text } from '@hh.ru/magritte-ui-typography';

import styles from './suggest.less';

interface ValueSelectHandler<K> {
    (value: string, data: K | undefined): void;
}

interface SectionRenderer<T extends DataSection['type'], K = unknown> {
    (
        section: DataSectionOfType<T>,
        sectionId: number,
        onValueSelect: ValueSelectHandler<K>,
        breakpoint: Breakpoint,
        length: number,
        haveBottomContent: boolean
    ): JSX.Element[] | JSX.Element;
}

const cellSectionRenderer: SectionRenderer<'cells'> = (section, sectionId, onValueSelect, breakpoint) => {
    return section.items.map((item) => {
        return (
            <Card
                style="blank"
                stretched
                hoverStyle="neutral"
                onMouseDown={(event) => {
                    event.preventDefault();
                }}
                onClick={(event) => {
                    event.preventDefault();
                    onValueSelect(item.value, item.data);
                }}
                actionCard
                padding={[Breakpoint.XS, Breakpoint.S].includes(breakpoint) ? 0 : 12}
                paddingTop={12}
                paddingBottom={12}
                borderRadius={12}
                key={`cell_${sectionId}_${item.value}`}
                data-qa="suggest-item-cell"
            >
                <Cell {...item.componentProps} onClick={undefined} />
            </Card>
        );
    });
};

const chipsSectionRenderer: SectionRenderer<'chips'> = (
    section,
    sectionId,
    onValueSelect,
    _,
    length,
    haveBottomContent
) => {
    return (
        <div
            className={classnames(styles.chipsSectionContainer, {
                // чипсы - не последний элемент или есть нижний контент
                [styles.chipsSectionContainerWithOtherItems]: sectionId !== length - 1 || haveBottomContent,
            })}
            key={`chips_section_${sectionId}`}
        >
            {section.items.map((item) => {
                return (
                    <CustomChip
                        {...(item.componentProps || {})}
                        key={`chips_${sectionId}_${item.value}`}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => onValueSelect(item.value, item.data)}
                        data-qa="suggest-item-chips"
                    >
                        {item.value}
                    </CustomChip>
                );
            })}
        </div>
    );
};

const delimiterSectionRenderer: SectionRenderer<'delimiter'> = (section, _, __, breakpoint) => {
    return [
        <Card
            style="blank"
            stretched
            padding={[Breakpoint.XS, Breakpoint.S].includes(breakpoint) ? 0 : 12}
            paddingTop={8}
            paddingBottom={8}
            key={`delimiter_${section.text}`}
            data-qa="suggest-item-delimiter"
        >
            <Text style="secondary" typography="label-3-regular">
                {section.text}
            </Text>
        </Card>,
    ];
};

const SECTION_TYPE_TO_RENDERER_MAP: { [K in DataSection['type']]: SectionRenderer<K> } = {
    cells: cellSectionRenderer,
    chips: chipsSectionRenderer,
    delimiter: delimiterSectionRenderer,
};

export const renderSection = <T, K extends DataSection['type']>(
    section: DataSectionOfType<K>,
    sectionId: number,
    onValueSelect: ValueSelectHandler<T>,
    breakpoint: Breakpoint,
    length: number,
    haveBottomContent: boolean
): JSX.Element[] => {
    const renderer = SECTION_TYPE_TO_RENDERER_MAP[section.type];
    const result = renderer(
        section,
        sectionId,
        onValueSelect as ValueSelectHandler<unknown>,
        breakpoint,
        length,
        haveBottomContent
    );
    return Array.isArray(result) ? result : [result];
};
